/* Graph of Life brand v1.0.0 · tokens.json */
// Canonical proof registry. Single source of truth for every proof claim,
// metric and boundary rendered on the homepage and in the Proof Library.
// Nothing about a proof system may be hardcoded in a component.

export const proofSystems = [
  {
    id: 'bluegraph',
    motif: 'lattice',
    previewLabel: 'governed planning units · committed traces',
    name: 'BlueGraph',
    featured: true,
    order: 1,
    decisionQuestion:
      'Where does the evidence support further diligence?',
    whatItDoes:
      'Spatial decision graph connecting place, evidence, caveats and bounded decision traces. Observed evidence can challenge the model directly.',
    evidenceStatus:
      'Operational governed graph. Every claim carries an explicit evidence tier, and committed decision traces are published for inspection.',
    evidenceState: 'operational',
    currentLimit:
      'Screening and evidence-governance infrastructure. Financeable credit, legal permission, payout triggers and final investment conclusions sit outside the current scope.',
    metrics: [
      '10,913 planning units',
      '97,573 graph nodes',
      '514,728 governed relationships',
      '24,316 governed claims',
      '41 units at full-rigor molecular evidence',
    ],
    source:
      'Six independent Gulf of California evidence streams across habitat, biodiversity, fisheries, oceanography, pressures and ecosystem services.',
    method: 'Governed spatial decision graph with per-claim evidence tiers and committed decision traces.',
    confidence: 'Screening-grade. Evidence tier stated per claim.',
    intendedUse: 'Exploratory screening, evidence governance and diligence preparation.',
    lastVerified: '2026-06',
    url: 'https://jaygut.github.io/blue-graph/',
    secondary: { label: 'Seaflower decision trace', url: 'https://jaygut.github.io/blue-graph/seaflower/' },
  },
  {
    id: 'ai-ecologist',
    motif: 'web',
    previewLabel: 'interaction network · keystone pulse',
    name: 'AI Ecologist',
    featured: true,
    order: 2,
    decisionQuestion:
      'Which species and relationships may carry disproportionate importance within an ecosystem?',
    whatItDoes:
      'Reconstructs and stress-tests ecological interaction networks, with benchmarking and uncertainty made explicit.',
    evidenceStatus:
      'Benchmarked against known food webs. Link prediction reported at 0.948 AUC on held-out interactions.',
    evidenceState: 'exploratory',
    currentLimit:
      'Inferred ecological relationships for investigation and field validation. Financial-risk calibration and underwriting sit outside the current scope.',
    metrics: ['0.948 link-prediction AUC', '91 species reconstructed to 1,033 interactions'],
    source: 'Published food-web datasets and species interaction records.',
    method: 'Graph learning over species interaction networks with held-out benchmarking.',
    confidence: 'Exploratory inference with observation still pending.',
    intendedUse: 'Identifying mechanistic hypotheses that warrant field validation.',
    lastVerified: '2026-06',
    url: 'https://jaygut.github.io/ai-ecologist-landing/',
  },
  {
    id: 'fluvion',
    motif: 'corridor',
    previewLabel: 'moisture corridor · downwind crop sinks',
    name: 'Fluvion',
    featured: true,
    order: 3,
    decisionQuestion:
      'How might forest change reach a distant crop?',
    whatItDoes:
      'Explores teleconnected forest, rainfall and crop dependencies across space, with held-out testing and stated model limitations.',
    evidenceStatus:
      'Reproduces the 2022 drought split. Held-out 2024 test reported at r = 0.45, with limitations published alongside the result.',
    evidenceState: 'indicative',
    currentLimit:
      'Indicative dependency and scenario evidence. Forecasting, prudential measurement and validated pricing sit outside the current scope.',
    metrics: ['Held-out 2024 test r = 0.45', 'Reproduces the 2022 drought split'],
    source: 'Atmospheric moisture tracking, land cover and agricultural yield records.',
    method: 'Moisture-transport dependency modelling with held-out temporal validation.',
    confidence: 'Indicative association with causality still unestablished.',
    intendedUse: 'Scenario framing and identification of dependency pathways for further validation.',
    lastVerified: '2026-06',
    url: 'https://jaygut.github.io/fluvion-landing/',
    secondary: { label: 'Soy belt corridor', url: 'https://jaygut.github.io/fluvion-landing/soy-belt.html' },
    tertiary: { label: 'Andes corridor', url: 'https://jaygut.github.io/fluvion-landing/andes.html' },
  },
  {
    id: 'methanet',
    motif: 'strata',
    previewLabel: 'sediment strata · methane pathway',
    name: 'MethaNet',
    featured: true,
    order: 4,
    decisionQuestion:
      'Which methane pathways deserve a closer look?',
    whatItDoes:
      'Organizes molecular evidence to screen biological pathways that could affect a blue-carbon diligence question.',
    evidenceStatus:
      'Molecular atlas assembled from 5,209 mapped genomes with 372 cross-ecosystem bridge links. Paired field validation remains pending.',
    evidenceState: 'screening',
    currentLimit:
      'Screening and triage only. Calibrated monitoring, reporting and verification require paired field validation and authoritative environmental context.',
    metrics: ['5,209 genomes mapped', '372 cross-ecosystem bridge links'],
    source: 'Public sediment and marine metagenome archives.',
    method: 'Genome-resolved pathway mapping across ecosystem contexts.',
    confidence: 'Screening-grade with field validation pending.',
    intendedUse: 'Triage of blue-carbon sites for deeper molecular and field investigation.',
    lastVerified: '2026-06',
    url: 'https://emergentbiome.earth/',
  },
  {
    id: 'upper-colorado',
    motif: 'gauges',
    previewLabel: '232 stations · published model losses',
    name: 'Upper Colorado Corridor',
    featured: false,
    order: 5,
    decisionQuestion:
      'Which parcels does a watershed depend on, and where does the model lose to a simpler baseline?',
    whatItDoes:
      'Names the parcels a watershed depends on, then tests its own scores against real monitoring stations and publishes where it underperforms.',
    evidenceStatus:
      'Tested against 232 monitoring stations. The public ledger reports the cases where a simpler baseline wins.',
    evidenceState: 'exploratory',
    currentLimit:
      'Exploratory watershed dependency evidence. Water rights, eligibility, pricing and validated allocation decisions sit outside the current scope.',
    metrics: ['232 monitoring stations', 'Public ledger of model losses'],
    source: 'Public watershed monitoring station records and parcel geometry.',
    method: 'Parcel-level dependency scoring with adversarial baseline comparison.',
    confidence: 'Exploratory. Reported with published failure cases.',
    intendedUse: 'Prioritizing watershed parcels for further validation and monitoring design.',
    lastVerified: '2026-06',
    url: 'https://jaygut.github.io/upper-colorado-corridor-landing/',
  },
  {
    id: 'bioprior',
    motif: 'spectrum',
    previewLabel: 'hyperspectral bands · separability threshold',
    name: 'BioPrior Engine',
    featured: false,
    order: 6,
    decisionQuestion:
      'Can hyperspectral satellite evidence separate field-verified biology from ocean-color noise?',
    whatItDoes:
      'Tests whether hyperspectral satellite bands can distinguish field-verified biological signal from ocean-color noise.',
    evidenceStatus:
      '1.18M biological labels assembled from 288K field samples across 10 sources. Separability remains under test.',
    evidenceState: 'exploratory',
    currentLimit:
      'Method development. Validated remote-sensing biological products, credits and monitoring standards sit outside the current scope.',
    metrics: ['1.18M biological labels', '288K field samples', '10 source archives'],
    source: 'Ten public field and remote-sensing archives.',
    method: 'Label harmonization and separability testing against field-verified biology.',
    confidence: 'Exploratory with separability still pending.',
    intendedUse: 'Determining whether a remote-sensing biological signal is worth pursuing.',
    lastVerified: '2026-06',
    url: 'https://jaygut.github.io/phyto-carbon-bioprior-engine/',
  },
];

export const proofCount = proofSystems.length;

export const featuredProofs = proofSystems.filter((p) => p.featured).sort((a, b) => a.order - b.order);

export const getProof = (id) => proofSystems.find((p) => p.id === id);

// Evidence states carry a glyph and a word, never color alone.
export const evidenceStates = {
  operational: { label: 'Operational', glyph: '\u25C6', tone: 'living' },
  indicative: { label: 'Indicative', glyph: '\u25B2', tone: 'caution' },
  exploratory: { label: 'Exploratory', glyph: '\u25CB', tone: 'caution' },
  screening: { label: 'Screening only', glyph: '\u25A0', tone: 'risk' },
};
